/** Synchronous shell for EEG detached pop-out windows (avoids about:blank flash). */

import {
  computeAnnotationListViewportHeightPx,
  EEG_ANNOTATION_LIST_VISIBLE_ROWS,
} from "./eegDetachedWindowMetrics";

export interface BootstrapEegDetachedWindowOptions {
  title: string;
  panelKey: string;
  compactToolbar?: boolean;
}

function buildDetachedPanelUrl(options: {
  title: string;
  panelKey: string;
}): string {
  const url = new URL("/eeg-detached.html", window.location.origin);
  url.searchParams.set("panel", options.panelKey);
  url.searchParams.set("title", options.title);
  return url.toString();
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function isEegDetachedShellDocument(doc: Document): boolean {
  if (doc.getElementById("eeg-panel-root")) return true;
  try {
    return doc.location.pathname.endsWith("/eeg-detached.html");
  } catch {
    return false;
  }
}

export function syncDetachedWindowLocation(
  win: Window,
  options: Pick<BootstrapEegDetachedWindowOptions, "title" | "panelKey">,
): void {
  if (win.closed) return;

  const targetUrl = buildDetachedPanelUrl(options);
  try {
    const current = win.location.href;
    if (current === targetUrl) return;
    if (
      current === "about:blank" ||
      !current.includes("/eeg-detached.html")
    ) {
      win.history.replaceState(null, "", targetUrl);
    }
  } catch {
    // Cross-origin or restricted popup — ignore.
  }
}

export function waitForDetachedShellRoot(
  win: Window,
  timeoutMs = 5000,
): Promise<HTMLElement | null> {
  if (win.closed) return Promise.resolve(null);

  const existing = win.document.getElementById("eeg-panel-root");
  if (existing) return Promise.resolve(existing);

  return new Promise((resolve) => {
    const started = Date.now();

    const finish = (root: HTMLElement | null) => {
      win.clearInterval(poll);
      try {
        win.removeEventListener("load", onLoad);
      } catch {
        /* window may be closing */
      }
      resolve(root);
    };

    const tryResolve = () => {
      if (win.closed) {
        finish(null);
        return;
      }
      const root = win.document.getElementById("eeg-panel-root");
      if (root) {
        finish(root);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        finish(null);
      }
    };

    const onLoad = () => tryResolve();
    try {
      win.addEventListener("load", onLoad, { once: true });
    } catch {
      /* ignore */
    }

    const poll = win.setInterval(tryResolve, 16);
    tryResolve();
  });
}

function applyWorkspaceThemeToDetachedWindow(win: Window): void {
  const theme =
    document.documentElement.getAttribute("data-eeg-workspace-theme") ||
    document.body.getAttribute("data-eeg-workspace-theme") ||
    "dark";
  const colorScheme = theme === "light" ? "light" : "dark";
  win.document.documentElement.setAttribute("data-eeg-workspace-theme", theme);
  win.document.body.setAttribute("data-eeg-workspace-theme", theme);
  win.document.documentElement.style.colorScheme = colorScheme;
  win.document.body.style.colorScheme = colorScheme;
}

function buildDetachedWindowStyles(compactToolbar: boolean): string {
  const listViewportPx = computeAnnotationListViewportHeightPx();
  const compactRules = compactToolbar
    ? `
          html.eeg-detached-window--compact-toolbar,
          html.eeg-detached-window--compact-toolbar body {
            height: 100dvh !important;
            min-height: 0 !important;
            overflow: hidden !important;
          }
          html.eeg-detached-window--compact-toolbar #eeg-panel-root {
            height: 100dvh !important;
            min-height: 0 !important;
          }
          .eeg-detached-panel-content--compact-toolbar {
            flex: 1 1 auto !important;
            height: 100% !important;
            min-height: 0 !important;
            overflow: hidden !important;
          }
          .eeg-detached-shell--compact-toolbar {
            height: 100% !important;
            max-height: 100dvh !important;
            padding: 4px !important;
            overflow: hidden !important;
          }
          .eeg-detached-shell--compact-toolbar .quick-action-toolbar--detached,
          .eeg-detached-shell--compact-toolbar .quick-action-toolbar-container {
            max-height: calc(100dvh - 8px) !important;
            overflow-y: auto !important;
            overscroll-behavior: contain;
          }
          .eeg-detached-shell--compact-toolbar .quick-action-toolbar-list {
            max-height: min(${listViewportPx}px, calc(100dvh - 128px));
            overflow-y: auto;
            overflow-x: hidden;
          }
        `
    : "";

  return `
          *, *::before, *::after { box-sizing: border-box; }
          :root {
            --clinical-eeg-chrome-bg: #10243a;
            --clinical-eeg-chrome-bg-elevated: #1e2d42;
            --clinical-eeg-chrome-border: #37506c;
            --clinical-eeg-chrome-border-subtle: #20344d;
            --clinical-eeg-chrome-text: #e2e8f0;
            --clinical-eeg-chrome-text-muted: #a8b4c4;
            --clinical-eeg-chrome-text-subtle: #64748b;
            --clinical-eeg-canvas-bg: #000000;
            --clinical-eeg-accent: #3b82f6;
            --clinical-eeg-focus-ring: #93c5fd;
            --clinical-eeg-btn-bg: rgba(30, 54, 82, 0.88);
            --clinical-eeg-btn-bg-hover: rgba(48, 77, 112, 0.94);
            --clinical-eeg-btn-border: #4f6f95;
            --clinical-eeg-btn-text: #e2e8f0;
            --clinical-eeg-popout-bg: rgba(14, 165, 233, 0.16);
            --clinical-eeg-popout-border: rgba(14, 165, 233, 0.42);
            --clinical-eeg-popout-text: #7dd3fc;
            --clinical-eeg-recent-bg: rgba(15, 23, 42, 0.58);
            --clinical-eeg-recent-border: rgba(148, 163, 184, 0.24);
            --clinical-eeg-recent-hover-bg: rgba(30, 64, 175, 0.34);
            --clinical-eeg-recent-hover-border: rgba(125, 211, 252, 0.7);
            --eeg-chrome-bg: var(--clinical-eeg-chrome-bg);
            --eeg-chrome-bg-elevated: var(--clinical-eeg-chrome-bg-elevated);
            --eeg-chrome-border: var(--clinical-eeg-chrome-border);
            --eeg-chrome-border-subtle: var(--clinical-eeg-chrome-border-subtle);
            --eeg-chrome-text: var(--clinical-eeg-chrome-text);
            --eeg-chrome-text-muted: var(--clinical-eeg-chrome-text-muted);
            --eeg-chrome-text-subtle: var(--clinical-eeg-chrome-text-subtle);
            --eeg-canvas-bg: var(--clinical-eeg-canvas-bg);
            --eeg-accent: var(--clinical-eeg-accent);
            --eeg-focus-ring: var(--clinical-eeg-focus-ring);
            --eeg-chrome-btn-bg: var(--clinical-eeg-btn-bg);
            --eeg-chrome-btn-bg-hover: var(--clinical-eeg-btn-bg-hover);
            --eeg-chrome-btn-border: var(--clinical-eeg-btn-border);
            --eeg-chrome-btn-text: var(--clinical-eeg-btn-text);
            --eeg-popout-bg: var(--clinical-eeg-popout-bg);
            --eeg-popout-border: var(--clinical-eeg-popout-border);
            --eeg-popout-text: var(--clinical-eeg-popout-text);
            --eeg-recent-bg: var(--clinical-eeg-recent-bg);
            --eeg-recent-border: var(--clinical-eeg-recent-border);
            --eeg-recent-hover-bg: var(--clinical-eeg-recent-hover-bg);
            --eeg-recent-hover-border: var(--clinical-eeg-recent-hover-border);
          }
          html, body {
            margin: 0; padding: 0;
            color-scheme: inherit;
            background: var(--eeg-chrome-bg) !important;
            color: var(--eeg-chrome-text);
            font-family: system-ui, -apple-system, sans-serif;
            height: auto;
            min-height: 100%;
            overflow: auto;
          }
          #eeg-panel-root {
            display: flex;
            flex-direction: column;
            height: 100%;
          }
          .eeg-detached-toolbar {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 6px 12px;
            background: var(--eeg-chrome-bg-elevated);
            border-bottom: 1px solid var(--eeg-chrome-border);
            flex-shrink: 0;
          }
          .eeg-detached-toolbar__title {
            font-size: 0.78rem;
            font-weight: 600;
            color: var(--eeg-chrome-text-muted);
            letter-spacing: 0.04em;
            text-transform: uppercase;
          }
          .eeg-detached-toolbar__actions {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            flex: 0 0 auto;
          }
          .eeg-detached-toolbar__front,
          .eeg-detached-toolbar__popin {
            padding: 3px 10px;
            font-size: 0.74rem;
            font-weight: 600;
            background: rgba(96,165,250,0.15);
            border: 1px solid rgba(96,165,250,0.35);
            border-radius: 5px;
            color: var(--eeg-focus-ring);
            cursor: pointer;
          }
          .eeg-detached-toolbar__front {
            padding-inline: 8px;
            background: var(--eeg-popout-bg);
            border-color: var(--eeg-popout-border);
            color: var(--eeg-popout-text);
          }
          .eeg-detached-panel-content {
            flex: 1 1 auto;
            overflow: auto;
            min-height: 0;
            display: flex;
            flex-direction: column;
          }
          .eeg-detached-shell {
            height: auto;
            max-height: none;
            flex: 1 1 auto;
            min-height: 0;
            overflow: auto;
            background: var(--eeg-chrome-bg);
            border: 1px solid var(--eeg-chrome-border);
            border-radius: 8px;
            padding: 8px;
          }
          .eeg-detached-panel-content > .eeg-dock-panel-chrome,
          .eeg-detached-shell .eeg-dock-panel-chrome {
            flex: 1 1 auto;
            min-height: 0;
            overflow: auto;
          }
          .eeg-viewer.eeg-detached-shell {
            height: auto !important;
            max-height: none !important;
            min-height: 0 !important;
            overflow: auto !important;
            contain: none !important;
          }
          ${compactRules}
        `;
}

function writeFallbackDetachedDocument(
  win: Window,
  options: BootstrapEegDetachedWindowOptions,
): HTMLElement | null {
  const { title, compactToolbar = false } = options;
  const documentTitle = `${title} — EEG Panel`;

  win.document.open();
  win.document.write(`
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>${escapeHtml(documentTitle)}</title>
        <style>${buildDetachedWindowStyles(compactToolbar)}</style>
      </head>
      <body>
        <div id="eeg-panel-root"></div>
      </body>
    </html>
  `);
  win.document.close();
  applyWorkspaceThemeToDetachedWindow(win);
  syncDetachedWindowLocation(win, options);
  return win.document.getElementById("eeg-panel-root");
}

export function bootstrapEegDetachedWindowDocument(
  win: Window,
  options: BootstrapEegDetachedWindowOptions,
): HTMLElement | null {
  if (win.closed) return null;

  const { title, panelKey, compactToolbar = false } = options;
  const documentTitle = `${title} — EEG Panel`;
  const existingRoot = win.document.getElementById("eeg-panel-root");
  const shellDocument = isEegDetachedShellDocument(win.document);

  if (!existingRoot) {
    if (shellDocument || win.document.readyState === "loading") {
      syncDetachedWindowLocation(win, options);
      return null;
    }
    return writeFallbackDetachedDocument(win, options);
  }

  win.document.title = documentTitle;
  let styleEl = win.document.querySelector<HTMLStyleElement>(
    "[data-eeg-detached-bootstrap]",
  );
  if (!styleEl) {
    styleEl = win.document.createElement("style");
    styleEl.setAttribute("data-eeg-detached-bootstrap", "true");
    win.document.head.appendChild(styleEl);
  }
  styleEl.textContent = buildDetachedWindowStyles(compactToolbar);

  win.document.documentElement.classList.add("eeg-detached-window");
  win.document.body.classList.add("eeg-detached-window");
  applyWorkspaceThemeToDetachedWindow(win);
  if (compactToolbar) {
    win.document.documentElement.classList.add(
      "eeg-detached-window--compact-toolbar",
    );
    win.document.body.classList.add("eeg-detached-window--compact-toolbar");
  }

  syncDetachedWindowLocation(win, { title, panelKey });

  return existingRoot;
}

export function copyHostStylesheetsToDetachedWindow(win: Window): void {
  if (win.closed) return;

  const hostLinks = Array.from(
    document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]'),
  );
  hostLinks.forEach((link) => {
    if (win.document.querySelector(`link[href="${link.href}"]`)) return;
    const newLink = win.document.createElement("link");
    newLink.rel = "stylesheet";
    newLink.href = link.href;
    win.document.head.appendChild(newLink);
  });

  const copiedStyleText = new Set(
    Array.from(
      win.document.querySelectorAll<HTMLStyleElement>("style[data-eeg-host-style]"),
    ).map((style) => style.textContent ?? ""),
  );

  document.querySelectorAll("style").forEach((style) => {
    const text = style.textContent ?? "";
    if (copiedStyleText.has(text)) return;
    copiedStyleText.add(text);
    const cloned = win.document.createElement("style");
    cloned.setAttribute("data-eeg-host-style", "true");
    cloned.textContent = text;
    if (style.media) cloned.media = style.media;
    win.document.head.appendChild(cloned);
  });

  appendDetachedWindowStyleOverrides(win);
}

export function subscribeDetachedWindowToHostStylesheets(
  win: Window,
): () => void {
  copyHostStylesheetsToDetachedWindow(win);
  if (win.closed || typeof MutationObserver === "undefined") {
    return () => undefined;
  }

  const observer = new MutationObserver(() => {
    if (win.closed) return;
    copyHostStylesheetsToDetachedWindow(win);
  });
  observer.observe(document.head, { childList: true, subtree: true });
  return () => observer.disconnect();
}

export function appendDetachedWindowStyleOverrides(win: Window): void {
  if (win.closed) return;
  if (win.document.querySelector("[data-eeg-detached-overrides]")) return;

  const listViewportPx = computeAnnotationListViewportHeightPx(
    EEG_ANNOTATION_LIST_VISIBLE_ROWS,
  );
  const style = win.document.createElement("style");
  style.setAttribute("data-eeg-detached-overrides", "true");
  style.textContent = `
    html, body {
      color-scheme: inherit;
      background: var(--eeg-chrome-bg, #10243a) !important;
    }
    html.eeg-detached-window--compact-toolbar,
    html.eeg-detached-window--compact-toolbar body {
      height: 100dvh !important;
      min-height: 0 !important;
      overflow: hidden !important;
    }
    html.eeg-detached-window--compact-toolbar #eeg-panel-root {
      height: 100dvh !important;
      min-height: 0 !important;
    }
    .eeg-detached-panel-content--compact-toolbar {
      flex: 1 1 auto !important;
      height: 100% !important;
      min-height: 0 !important;
      overflow: hidden !important;
    }
    .eeg-detached-shell--compact-toolbar {
      height: 100% !important;
      max-height: 100dvh !important;
      padding: 4px !important;
      background: var(--eeg-chrome-bg, #10243a) !important;
      overflow: hidden !important;
    }
    .eeg-viewer.eeg-detached-shell:not(.eeg-detached-shell--compact-toolbar) {
      height: auto !important;
      max-height: none !important;
      overflow: auto !important;
      contain: none !important;
    }
    .eeg-detached-panel-content > .eeg-dock-panel-chrome,
    .eeg-detached-shell .eeg-dock-panel-chrome {
      flex: 1 1 auto;
      min-height: 0;
      overflow: auto;
    }
    .eeg-detached-shell--compact-toolbar .quick-action-toolbar--detached,
    .eeg-detached-shell--compact-toolbar .quick-action-toolbar-container {
      max-height: calc(100dvh - 8px) !important;
      overflow-y: auto !important;
      overscroll-behavior: contain;
    }
    .eeg-detached-shell--compact-toolbar .quick-action-toolbar-list {
      max-height: min(${listViewportPx}px, calc(100dvh - 128px));
      overflow-y: auto;
      overflow-x: hidden;
    }
  `;
  win.document.head.appendChild(style);
}
